import { useCallback, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { t } from "@bookingcom/lingojs-core";
import { useI18n } from "@bookingcom/lingojs-react";
import { goal } from "@flights/et-universal";
import { flightsLocalStore } from "@flights/web-api-utils-universal";
import { clientFetch } from "@flights/client-fetch";
import { trackMetaLandingsCustomGoalsV2 } from "../../../utils/experiments/track-meta-landings-v2.js";
import { CURRENT_ORDER_ID_KEY, PRE_OPTED_ANCILLARIES_QUERY_PARAMS } from "../../../constants.js";
import useGetUrlParams from "../../../hooks/useGetUrlParams.js";
import { getPreOptedAncillaries } from "../../../ancillaries/store/extraProducts/selectors.js";
import { preOptedAncillariesURLParams } from "../../../utils/preOptedAncillaries.js";
import useCreateUrl from "../../../hooks/useCreateUrl.js";
import { getFlightDetailsFetchStatus } from "../../../store/flightDetails/selectors.js";
import useClientMetrics from "../../../hooks/useClientMetrics.js";
import useGlobalContext from "../../../hooks/useGlobalContext.js";
import useEventTracking from "../../../hooks/useEventTracking.js";
import useRetryFlightDetailsApi from "../../../hooks/useRetryFlightDetailsApi.js";
import useProfiler from "../../../hooks/useProfiler.js";
import { getSearchType } from "../../../store/search/selectors.js";
import { getModalTitle } from "./components/flightDetailsUtils.js";
import useAvailableBrandedFares from "../../../branded-fares/hooks/useAvailableBrandedFares.js";
export function useFlightDetailsInnerDesktopController(props) {
    const { flightToken, flight, fetchStatus, onClose, alternativeFaresFlow, hasPrefetchedBrandedFareOffers } = props;
    const i18n = useI18n();
    const history = useHistory();
    const { createUrl } = useCreateUrl();
    const { requestId } = useGlobalContext();
    const { getUrlParam } = useGetUrlParams();
    const { trackClientMetric } = useClientMetrics();
    const { isRetryableFlightDetails } = useRetryFlightDetailsApi();
    const trackV2 = useEventTracking("flight_details", requestId);
    const { isLoggedInAuthLow } = useProfiler();
    const preOptedAncillaries = useSelector(getPreOptedAncillaries);
    const searchType = useSelector(getSearchType);
    const flightDetailsFetchStatus = useSelector(getFlightDetailsFetchStatus);
    const brandedFareOffers = useAvailableBrandedFares();
    const [isModalOpen, setModalOpen] = useState(true);
    const isLoggedInAuthLowFn = isLoggedInAuthLow;
    const isError = useMemo(() => {
        return fetchStatus === "failed" && !(flight && isRetryableFlightDetails());
    }, [fetchStatus, flight, isRetryableFlightDetails]);
    const goToCheckout = useCallback((token) => {
        let preOpted = preOptedAncillaries;
        if (getUrlParam(PRE_OPTED_ANCILLARIES_QUERY_PARAMS.flexibleTicket)) {
            preOpted = { ...preOpted, flexibleTicket: true };
        }
        if (getUrlParam(PRE_OPTED_ANCILLARIES_QUERY_PARAMS.cancelForAnyReason)) {
            preOpted = { ...preOpted, cancelForAnyReason: true };
        }
        const urlParams = preOptedAncillariesURLParams(preOpted);
        if (!preOpted.cancelForAnyReason && !preOpted.flexibleTicket && brandedFareOffers?.length > 0) {
            const baseOfferToken = getUrlParam("baseOfferToken");
            if (baseOfferToken)
                urlParams.set("baseOfferToken", baseOfferToken);
        }
        if (hasPrefetchedBrandedFareOffers) {
            urlParams.set("hasPrefetchedBrandedFareOffers", "true");
        }
        const url = createUrl(`/checkout/start/${token}?${urlParams}`);
        setTimeout(() => history.push(url));
    }, [preOptedAncillaries, createUrl, history, getUrlParam, brandedFareOffers, hasPrefetchedBrandedFareOffers]);
    const onFlightSelect = useCallback(() => {
        if (flight) {
            const orderId = flightsLocalStore.get(CURRENT_ORDER_ID_KEY);
            if (alternativeFaresFlow) {
                void clientFetch("/api/order/track-alternative-fares-event", {
                    method: "POST",
                    body: JSON.stringify({
                        eventType: "ALTERNATIVE_FARE_SELECTED",
                        orderId,
                        selectedOffer: flight
                    }),
                    credentials: "same-origin",
                    headers: {
                        "Content-Type": "application/json"
                    }
                });
            }
            trackMetaLandingsCustomGoalsV2(3, "desktop");
            if (flightDetailsFetchStatus !== "success") {
                trackClientMetric("flight_details_quick_checkout_start");
            }
            trackV2("select_flight_details_next");
            goal("flights_flight_details_action_click");
            setModalOpen(false);
            goToCheckout(flightToken);
        }
    }, [flight, alternativeFaresFlow, flightDetailsFetchStatus, trackClientMetric, trackV2, goToCheckout, flightToken]);
    const onModalClose = useCallback(() => {
        if (isModalOpen) {
            trackV2("click_back_button");
            onClose();
        }
    }, [isModalOpen, trackV2, onClose]);
    const modalTitle = useMemo(() => {
        return getModalTitle(i18n, flight?.segments, searchType);
    }, [i18n, flight?.segments, searchType]);
    const shouldShowWishlistWidget = useMemo(() => {
        return isLoggedInAuthLow();
    }, [isLoggedInAuthLow]);
    const campaignBadges = useMemo(() => {
        return fetchStatus === "success" ? flight?.campaignDisplay?.badges ?? [] : [];
    }, [fetchStatus, flight?.campaignDisplay?.badges]);
    const errorTitle = i18n.trans(t("flights_error_unavailable"));
    const errorBody = i18n.trans(t("flights_error_unavailable_body"));
    const errorButtonLabel = i18n.trans(t("flights_search_action"));
    return {
        isError,
        isModalOpen,
        modalTitle,
        shouldShowWishlistWidget,
        campaignBadges,
        onFlightSelect,
        onModalClose,
        isLoggedInAuthLow: isLoggedInAuthLowFn,
        trackV2,
        flightDetailsFetchStatus,
        isRetryableFlightDetails,
        errorTitle,
        errorBody,
        errorButtonLabel
    };
}
